import React from "react";
import type { VisitorOrderRecord } from "./visitorSubmittedOrders";

export type VisitorProfileLite = {
  name?: string;
  phone?: string;
  email?: string;
};

type ShelfCartLine = {
  productKey?: string;
  productId?: string;
  productUniqueId?: string;
  productName?: string;
  designNum?: string;
  productImage?: string;
  quantity?: number;
  sellPriceMrpSnapshot?: string;
};

type ShelfShortlistLine = {
  productKey?: string;
  productUniqueId?: string;
  productName?: string;
  designNum?: string;
  productImage?: string;
};

function parsePriceSnapshot(raw?: string): number {
  if (raw == null || raw === "") return 0;
  const n = parseFloat(String(raw).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatInr(n: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

function clampQty(n: number): number {
  return Math.min(999, Math.max(1, Math.trunc(n)));
}

function orderCartLineTotals(lines: unknown[]): { subtotal: number; pieces: number; lineCount: number } {
  let subtotal = 0;
  let pieces = 0;
  let lineCount = 0;
  for (const raw of lines) {
    const line = raw as ShelfCartLine;
    const q = clampQty(Number(line.quantity) || 1);
    pieces += q;
    lineCount += 1;
    subtotal += parsePriceSnapshot(line.sellPriceMrpSnapshot) * q;
  }
  return { subtotal, pieces, lineCount };
}

type SubmittedOrderCardProps = {
  ord: VisitorOrderRecord;
  isOpen: boolean;
  onToggle: () => void;
  profile: VisitorProfileLite | null;
};

const SubmittedOrderCard: React.FC<SubmittedOrderCardProps> = ({
  ord,
  isOpen,
  onToggle,
  profile,
}) => {
  const ocart = Array.isArray(ord.cartLines) ? ord.cartLines : [];
  const osl = Array.isArray(ord.shortlistLines) ? ord.shortlistLines : [];
  const cartAgg = orderCartLineTotals(ocart);
  const modeLabel =
    ord.mode === "both"
      ? "Cart & shortlist"
      : ord.mode === "cart"
        ? "Cart"
        : "Shortlist";
  const when = ord.submittedAtIso ? new Date(ord.submittedAtIso).toLocaleString("en-IN") : "";
  const displayName = (ord.clientName || profile?.name || "").trim() || "—";
  const displayPhone = (ord.clientPhone || profile?.phone || "").trim() || "—";
  const displayEmail = (ord.clientEmail || profile?.email || "").trim() || "—";

  let headerSummary = "";
  if (ocart.length > 0) {
    headerSummary = `${formatInr(cartAgg.subtotal)} · ${cartAgg.lineCount} priced line${cartAgg.lineCount === 1 ? "" : "s"}`;
    if (osl.length > 0) {
      headerSummary += ` · ${osl.length} shortlist`;
    }
  } else if (osl.length > 0) {
    headerSummary = `${osl.length} shortlist item${osl.length === 1 ? "" : "s"}`;
  }

  const invoiceId = `hkp-order-invoice-${ord.id}`;

  return (
    <div className="hkp-visitor-order-card">
      <button
        type="button"
        className="hkp-visitor-order-toggle"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={invoiceId}
        id={`${invoiceId}-btn`}
      >
        <span className="hkp-visitor-order-chev" aria-hidden>
          {isOpen ? "▼" : "▶"}
        </span>
        <span className="hkp-visitor-order-toggle-main">
          <span className="hkp-visitor-order-badge">{modeLabel}</span>
          <span className="hkp-visitor-order-when">{when}</span>
        </span>
        <span className="hkp-visitor-order-sum">{headerSummary}</span>
      </button>

      {!isOpen ? (
        <p className="hkp-visitor-order-collapsed-hint">
          Tap to view full invoice · Collection <code>{ord.catalogueId}</code>
        </p>
      ) : null}

      {isOpen ? (
        <div className="hkp-visitor-order-invoice" id={invoiceId} role="region" aria-labelledby={`${invoiceId}-btn`}>
          <div className="hkp-visitor-order-invoice-meta">
            <div className="hkp-visitor-order-invoice-title">Condensed invoice</div>
            <dl className="hkp-visitor-order-dl">
              <div>
                <dt>Name</dt>
                <dd>{displayName}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{displayPhone}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{displayEmail}</dd>
              </div>
              <div className="hkp-visitor-order-dl-span">
                <dt>Collection</dt>
                <dd>
                  <code>{ord.catalogueId}</code>
                </dd>
              </div>
              <div className="hkp-visitor-order-dl-span">
                <dt>Submitted</dt>
                <dd>{when || "—"}</dd>
              </div>
            </dl>
          </div>

          {ocart.length > 0 ? (
            <div className="hkp-visitor-order-block">
              <h4 className="hkp-visitor-order-block-title">Cart</h4>
              <div className="hkp-visitor-account-table-wrap">
                <table className="hkp-visitor-invoice-table hkp-visitor-invoice-table--readonly">
                  <thead>
                    <tr>
                      <th className="hkp-inv-col-thumb" scope="col">
                        {" "}
                      </th>
                      <th className="hkp-inv-col-product" scope="col">
                        Product
                      </th>
                      <th className="hkp-inv-col-unit" scope="col">
                        Unit
                      </th>
                      <th className="hkp-inv-col-qty" scope="col">
                        Qty
                      </th>
                      <th className="hkp-inv-col-amt" scope="col">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ocart.map((raw, idx) => {
                      const line = raw as ShelfCartLine;
                      const pk = line.productKey || "";
                      const qty = clampQty(Number(line.quantity) || 1);
                      const unit = parsePriceSnapshot(line.sellPriceMrpSnapshot);
                      const lineTotal = unit * qty;
                      const img = line.productImage?.trim();
                      return (
                        <tr key={pk || line.productUniqueId || idx}>
                          <td className="hkp-inv-col-thumb">
                            {img ? (
                              <img src={img} alt="" className="hkp-visitor-invoice-thumb" />
                            ) : (
                              <div className="hkp-visitor-invoice-thumb-ph" aria-hidden />
                            )}
                          </td>
                          <td className="hkp-inv-col-product">
                            <div className="hkp-inv-product-name">
                              {line.productName || line.productUniqueId || "Product"}
                            </div>
                            {line.designNum ? (
                              <div className="hkp-inv-product-meta">Design {line.designNum}</div>
                            ) : null}
                          </td>
                          <td className="hkp-inv-col-unit">{unit > 0 ? formatInr(unit) : "—"}</td>
                          <td className="hkp-inv-col-qty">{qty}</td>
                          <td className="hkp-inv-col-amt">{unit > 0 ? formatInr(lineTotal) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="hkp-visitor-order-cart-totals">
                <span>Pieces (cart)</span>
                <span>{cartAgg.pieces}</span>
                <span>Subtotal</span>
                <span className="hkp-visitor-order-cart-totals-strong">{formatInr(cartAgg.subtotal)}</span>
              </div>
            </div>
          ) : null}

          {osl.length > 0 ? (
            <div className="hkp-visitor-order-block">
              <h4 className="hkp-visitor-order-block-title">Shortlist</h4>
              <div className="hkp-visitor-account-table-wrap">
                <table className="hkp-visitor-invoice-table hkp-visitor-invoice-table--readonly">
                  <thead>
                    <tr>
                      <th className="hkp-inv-col-thumb" scope="col">
                        {" "}
                      </th>
                      <th className="hkp-inv-col-product" scope="col">
                        Product
                      </th>
                      <th className="hkp-inv-col-unit" scope="col">
                        Note
                      </th>
                      <th className="hkp-inv-col-qty" scope="col">
                        Qty
                      </th>
                      <th className="hkp-inv-col-amt" scope="col">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {osl.map((raw, idx) => {
                      const line = raw as ShelfShortlistLine;
                      const pk = line.productKey || "";
                      const img = line.productImage?.trim();
                      return (
                        <tr key={pk || line.productUniqueId || idx}>
                          <td className="hkp-inv-col-thumb">
                            {img ? (
                              <img src={img} alt="" className="hkp-visitor-invoice-thumb" />
                            ) : (
                              <div className="hkp-visitor-invoice-thumb-ph" aria-hidden />
                            )}
                          </td>
                          <td className="hkp-inv-col-product">
                            <div className="hkp-inv-product-name">
                              {line.productName || line.productUniqueId || "Product"}
                            </div>
                            {line.designNum ? (
                              <div className="hkp-inv-product-meta">Design {line.designNum}</div>
                            ) : null}
                          </td>
                          <td className="hkp-inv-col-unit">Saved for later</td>
                          <td className="hkp-inv-col-qty">—</td>
                          <td className="hkp-inv-col-amt">—</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

type VisitorSubmittedOrdersSectionProps = {
  orderLog: VisitorOrderRecord[];
  expandedOrders: Record<string, boolean>;
  onToggleOrder: (orderId: string) => void;
  profile: VisitorProfileLite | null;
  /** Extra class on the outer section (e.g. margin). */
  className?: string;
};

/**
 * Expandable list of locally stored “submitted to studio” snapshots (condensed invoices).
 */
export const VisitorSubmittedOrdersSection: React.FC<VisitorSubmittedOrdersSectionProps> = ({
  orderLog,
  expandedOrders,
  onToggleOrder,
  profile,
  className = "",
}) => {
  if (orderLog.length === 0) return null;

  return (
    <section className={`hkp-visitor-account-card hkp-visitor-account-orders mb-4 ${className}`.trim()}>
      <h2 className="hkp-visitor-account-h2 hkp-visitor-orders-heading">Submitted to studio</h2>
      <p className="hkp-visitor-account-lead small mb-3">
        Tap an order to open a condensed invoice with products, prices, and your contact details. Each send
        clears that part of your draft so you can start a new cart or shortlist.
      </p>
      <div className="hkp-visitor-orders-stack">
        {orderLog.map((ord) => (
          <SubmittedOrderCard
            key={ord.id}
            ord={ord}
            isOpen={!!expandedOrders[ord.id]}
            onToggle={() => onToggleOrder(ord.id)}
            profile={
              profile
                ? {
                    name: profile.name,
                    phone: profile.phone,
                    email: profile.email,
                  }
                : null
            }
          />
        ))}
      </div>
    </section>
  );
};
